import type { Dayjs } from 'dayjs';
import type { DefaultOptionType } from 'rc-select/lib/Select';
import { base64 } from '@scure/base';

import dayjs from '~/dayjs';

import type { ColumnDefinition, ViewSlug } from '~/types/components';
import type { EmailCampaignLink } from '~/types/models';

export function classNames(classes: Record<string, boolean>): string {
  return Object.entries(classes)
    .filter(([_key, value]) => value)
    .map(([key, _value]) => key)
    .join(' ');
}

export async function delay(ms: number = 100): Promise<void> {
  await new Promise((resolve, _reject) => {
    setTimeout(resolve, ms);
  });
}

export function filterOption(input: string, option?: {
  label: string | React.ReactNode;
  filterName?: string;
}): boolean {
  if (option?.filterName) {
    return normalizeString(option?.filterName).includes(normalizeString(input));
  }

  if (!option?.label || typeof option.label !== 'string') {
    return false;
  }

  return normalizeString(option?.label).includes(normalizeString(input));
}

export function generateEmailCampaignLinkURL(campaignId: string, link: Partial<EmailCampaignLink>): string {
  let key = '';

  if (link.tagId) {
    key = `${campaignId}:${link.tagId ?? ''}`;
  } else if (link.result) {
    key = `${campaignId}:${link.result ?? ''}`;
  }

  const textEncoder = new TextEncoder();
  const uint8Array = textEncoder.encode(key);
  const base64Str = base64.encode(uint8Array);

  return `${process.env.REACT_APP_BASE_URL ?? ''}/pointage/${base64Str}`;
}

export function getWithParamString<R, T>(columns: Array<ColumnDefinition<R, T>>, context: ViewSlug): string {
  return columns
    .filter((c) => {
      if (c.type === 'collection') {
        return !c.lazy && (!c.only || c.only.includes(context));
      }

      if (c.type === 'item') {
        return !c.lazy && (!c.only || c.only.includes(context));
      }

      if (c.type === 'count') {
        return (!c.only || c.only.includes(context));
      }

      if (typeof c.with === 'string') {
        return (!c.only || c.only.includes(context));
      }

      return false;
    })
    .map((c) => c.with ?? c.key)
    .join(',');
}

export function labelsToOptions<T extends string>(input: Record<T, string>, nullLabel?: string): DefaultOptionType[] {
  const entries = Object.entries<string>(input);
  if (nullLabel) {
    entries.unshift(['', nullLabel]);
  }

  return entries.map(([value, label]) => ({
    id: value,
    label,
    value,
  }));
}

export function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

export function numberFormat(source: number | string): string {
  let value = source;
  if (typeof source === 'string') {
    value = Number.parseFloat(source);
    if (isNaN(value)) {
      throw new Error('Invalid number passed to numberFormat');
    }
  }

  if (typeof value !== 'number') {
    throw new Error('Invalid number passed to numberFormat');
  }

  return Intl.NumberFormat('fr').format(value);
}

export function parseDateRangeFilter(filter: string): [Dayjs, Dayjs] | [Dayjs, null] | [null, Dayjs] | [null, null] {
  const dateRegex = '([0-9]{4}-[0-9]{2}-[0-9]{2}( [0-9]{1,2}:[0-9]{2}(:[0-9]{2})?)?)';
  const intervalRegex = 'P([0-9]+Y)?([0-9]+M)?([0-9]+W)?([0-9]+D)?(T([0-9]+H)?([0-9]+M)?([0-9]+S)?)?';

  const dateRangeRegex = new RegExp(`^(?<start>${dateRegex})?:(?<end>${dateRegex})?$`);
  const dateMatches = filter.match(dateRangeRegex);
  if (dateMatches?.groups) {
    return [
      dateMatches.groups.start ? dayjs(dateMatches.groups.start) : null,
      dateMatches.groups.end ? dayjs(dateMatches.groups.end) : null,
    ];
  }

  const intervalRangeRegex = new RegExp(`^(?<start>${intervalRegex})?:(?<end>${intervalRegex})?$`);
  const intervalMatches = filter.match(intervalRangeRegex);
  if (intervalMatches?.groups) {
    const startIntervalSeconds = intervalMatches.groups.start ? dayjs.duration(intervalMatches.groups.start).asSeconds() : null;
    const endIntervalSeconds = intervalMatches.groups.end ? dayjs.duration(intervalMatches.groups.end).asSeconds() : null;

    return [
      startIntervalSeconds ? dayjs().subtract(startIntervalSeconds, 'seconds') : null,
      endIntervalSeconds ? dayjs().add(endIntervalSeconds, 'seconds') : null,
    ];
  }

  return [null, null];
}

export function percentage(num: number, denum: number): number {
  if (denum === 0) {
    return 0;
  }

  return (num / denum) * 100;
}

export function stringToColor(val: string): string {
  let hash = 0;
  val.split('').forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });

  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += value.toString(16).padStart(2, '0');
  }

  return color;
}

export function stripHtml(value: string | null): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = value ?? '';
  if (tmp.textContent) {
    return tmp.textContent;
  }

  if (tmp.innerText) {
    return tmp.innerText;
  }

  return '';
}

export function tc(definition: string, count: number): string {
  const parts = definition.split('|');
  switch (parts.length) {
    case 2:
      parts.unshift(parts[0]);
      break;
    case 1:
      parts.push(parts[0]);
      parts.push(parts[0]);
      break;
  }

  switch (count) {
    case 0:
      return parts[0].replace('{count}', `${count}`);
    case 1:
      return parts[1].replace('{count}', `${count}`);
    default:
      return parts[2].replace('{count}', `${count}`);
  }
}
